import Component from '@glimmer/component'
import { service } from '@ember/service'
import { eq } from 'ember-truth-helpers'
import type { IntlService } from 'ember-intl'
import { formatNumber } from 'district-ui-client/utils/giraffe/formatter'
import { AwardsEarnedRibbon } from 'district-ui-client/components/charts/dashboard/awards-earned/ribbons/ribbon'
import ribbonGold from '@images/awards-earned/ribbon-gold.svg'
import ribbonSilver from '@images/awards-earned/ribbon-silver.svg'
import ribbonBronze from '@images/awards-earned/ribbon-bronze.svg'
import type { AwardsEarnedData } from 'district-ui-client/utils/student-events/fetchers/awards-earned'

const RIBBON_COLORS = ['gold', 'silver', 'bronze'] as const
type RibbonColor = (typeof RIBBON_COLORS)[number]

export interface RibbonViewModel {
  value: string | number
  label: string
  color: RibbonColor
  legend?: string
  legendClassNames?: string
  src: string
}

interface Signature {
  Element: HTMLDivElement
  Args: {
    data: AwardsEarnedData
    withLegend?: boolean
  }
}

const ribbonData: Record<
  RibbonColor,
  {
    labelKey: string
    legend: string
    legendClassNames: string
    src: string
  }
> = {
  gold: {
    labelKey: 'awards.gold',
    legend: '91-100%',
    legendClassNames: 'bg-medal-gold',
    src: ribbonGold,
  },
  silver: {
    labelKey: 'awards.silver',
    legend: '80-90%',
    legendClassNames: 'bg-medal-silver',
    src: ribbonSilver,
  },
  bronze: {
    labelKey: 'awards.bronze',
    legend: '65-79%',
    legendClassNames: 'bg-medal-bronze',
    src: ribbonBronze,
  },
}

export class AwardsEarnedRibbons extends Component<Signature> {
  @service intl!: IntlService

  get ribbons(): RibbonViewModel[] {
    const data = this.args.data
    const showLegend = Boolean(this.args.withLegend)

    return RIBBON_COLORS.map((color) => {
      const info = ribbonData[color]
      return {
        value: formatNumber(data[color], 0),
        label: this.intl.t(info.labelKey),
        color,
        legend: showLegend ? info.legend : undefined,
        legendClassNames: showLegend ? info.legendClassNames : undefined,
        src: info.src,
      }
    })
  }

  <template>
    <div
      class="mx-auto flex h-full max-w-md items-center justify-center gap-1 px-8 py-6"
      data-test-awards-earned-ribbons
      ...attributes
    >
      {{#each this.ribbons as |ribbon|}}
        <AwardsEarnedRibbon class="w-1/3 {{if (eq ribbon.color 'silver') 'mt-12' '-mt-12'}}" @ribbon={{ribbon}} />
      {{/each}}
    </div>
  </template>
}

export default AwardsEarnedRibbons
